import { Controller } from "@hotwired/stimulus"
import type { TurboBeforeFetchRequestEvent } from "@hotwired/turbo"
import Confirmation, { ConfirmationType } from "../confirmation"

type ActionType = "delete" | "archive" | "remove" | "cancel" | "recover" | "backfill"

const ACTION_CONFIG: Record<ActionType, { style: ConfirmationType; button: string }> = {
  delete: { style: "danger", button: "Yes, delete!" },
  archive: { style: "warning", button: "Yes, archive!" },
  remove: { style: "danger", button: "Yes, remove!" },
  cancel: { style: "danger", button: "Yes, cancel it!" },
  recover: { style: "info", button: "Yes, recover!" },
  backfill: { style: "warning", button: "Yes, backfill!" },
}

/**
 * Generic controller for confirming actions before form submission.
 *
 * Usage:
 *   <!-- Simple delete confirmation -->
 *   <form data-controller="confirm-action"
 *         data-confirm-action-action-value="delete"
 *         data-confirm-action-item-value="this auto reply"
 *         data-action="turbo:before-fetch-request->confirm-action#confirm">
 *     <button type="submit">Delete</button>
 *   </form>
 *
 *   <!-- Bulk archive -->
 *   <form data-controller="confirm-action"
 *         data-confirm-action-action-value="archive"
 *         data-confirm-action-item-value="responses"
 *         data-confirm-action-bulk-value="true"
 *         data-action="turbo:before-fetch-request->confirm-action#confirm">
 *     <button type="submit">Archive Selected</button>
 *   </form>
 */
export default class ConfirmActionController extends Controller {
  static values = {
    action: String,
    item: { type: String, default: "this item" },
    bulk: { type: Boolean, default: false },
  }

  declare actionValue: ActionType
  declare hasActionValue: boolean
  declare itemValue: string
  declare bulkValue: boolean

  async confirm(event: TurboBeforeFetchRequestEvent) {
    event.preventDefault()

    if (!this.hasActionValue) {
      throw new Error("confirm-action controller requires data-confirm-action-action-value")
    }

    const config = ACTION_CONFIG[this.actionValue]
    if (!config) {
      throw new Error(`Unknown action type: ${this.actionValue}. Valid types: ${Object.keys(ACTION_CONFIG).join(", ")}`)
    }

    const text = this.bulkValue
      ? `Are you sure you want to ${this.actionValue} the selected ${this.itemValue}?`
      : `Are you sure you want to ${this.actionValue} ${this.itemValue}?`

    const confirmed = await Confirmation.confirm({
      text,
      confirmButtonText: config.button,
      type: config.style,
    })

    if (confirmed) {
      event.detail.resume(null)
    }
  }
}
